import type { CmafSegmentIterator, CmafSegmentSource, CmafSegment } from './mseBuffering';
import type { Observable} from '@mimir/common/util/observable';
import { ObservableState } from '@mimir/common/util/observable';
import { divideFuzzy, fractionToFuzzy, humanReadableRange, humanReadableRanges, integerToFuzzy, isRangeCovered, multiplyFuzzy } from '@mimir/common/util/fuzzyMath';
import { isAbortError } from '@mimir/common/util/abortControl';
import type { VideoApiActions } from '../util/apiActions';
import { liveItemMediaMonitoring } from './liveItemMediaMonitoring';
import type { PlayableItemGetOptions } from '@mimir/common/dto/playableItemDto';
import { exceptionToString } from '@mimir/common/util/exception';
import type { Logger } from '@mimir/common/types/logger';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import type { SegmentUrlResolver } from '../util/segmentUrlCache';
import { readWholeStream } from '@mimir/common/util/readableStream';
import { invariant } from '@mimir/common/util/invariant';
import type { Cleanable } from '@mimir/common/util/cleanable';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import type { Fraction } from '@mimir/common/util/fractionType';
import type { MediaAvailability } from '@mimir/common/dto/mediaAvailability';
import { CodecString, StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { FuzzyBounds, FuzzyValue } from '@mimir/common/util/fuzzyNumbers';
import type { AsyncTransmuxerProvider, AsyncTransmuxer } from './asyncTransmuxer';
import { PrefixedLogger } from '@mimir/common/log/prefixedLogger';
import { throwIfAborted } from '@mimir/common/util/abortSignal';
import type { TsSegmentInfo } from '@mimir/common/mpegts/inspect';

export interface TsAudioControllerFlacDownmix {
  kind: 'flacDownmix',
  matrix: Observable<number[][]>,
}

export type TsAudioController = TsAudioControllerFlacDownmix;

export type SoloAudioStream = (bytes: Uint8Array, audioStreamIndexToKeep: number) => void;

export function createCmafSegmentSourcesForItem(
  services: {
    api: VideoApiActions,
    fetch: BasicFetch,
    logger: Logger,
    urlResolver: SegmentUrlResolver,
    asyncTransmuxerProvider: AsyncTransmuxerProvider,
    getPeakOffset: () => number | undefined,
    addPeaksDelta: (peakOffset: number | undefined, peaksDeltaArrays: string[] | null | undefined) => number,
    inspectTs: (bytes: Uint8Array, firstSegment: boolean) => TsSegmentInfo | null,
  },
  playerDebugId: string,
  itemId: string,
  media: MediaAvailability,
  initialItemDuration: Fraction,
  streamRoleIds: StreamRoleId[],
  abortSignal: AbortSignal,
  tsAudioController: TsAudioController,
  audioChannelCount: ObservableState<number | null>,
  playableItemGetOptions: PlayableItemGetOptions,
): CmafSegmentSource[] {
  const { api, asyncTransmuxerProvider, fetch, logger, urlResolver, getPeakOffset, addPeaksDelta } = services;

  logger.debug('createCmafSegmentSourceForItem: start');

  const itemDuration = new ObservableState(integerToFuzzy(0));
  const segmentCount = new ObservableState(media?.segmentCount ?? 0);

  const updateFromMediaAvailability = (media: MediaAvailability, duration: Fraction) => {
    segmentCount.value = media.segmentCount;
    itemDuration.value = fractionToFuzzy(duration);
  };

  updateFromMediaAvailability(media, initialItemDuration);

  logger.debug(`createCmafSegmentSourceForItem: Will use streamRoleIds=${streamRoleIds}`);
  const cmafSegmentSources : CmafSegmentSource[] = [];
  for (const streamRoleId of streamRoleIds) {
    logger.debug(`createCmafSegmentSourceForItem: creating CMAF source for streamRoleId=${streamRoleId}`);

    const description = `mimir:${itemId}:${streamRoleId}`;

    const fetchSegment : FetchSegment = async (abortSignal: AbortSignal, segmentIndex: number) => {
      logger.debug(`fetchSegment(${segmentIndex})`);
      const url = await urlResolver.getMediaSegmentUrl(itemId, streamRoleId, segmentIndex);
      const response = await fetch(url, { signal: abortSignal });
      if (!response.ok) {
        throw new Error(`Failed to fetch segment: ${response.status} ${response.statusText}.`);
      }
      if (response.body === null) {
        throw new Error('Expected body in segment response');
      }
      return await readWholeStream(response.body);
    };

    const fetchSegmentWithPrefetch = makeSegmentPrefetcher(logger, fetchSegment, 5, segmentCount.getObservable());

    switch (streamRoleId) {
      case StreamRoleId.BrowsePreviewVideo:
      case StreamRoleId.BrowsePreviewVideoAudioDownMix:
      case StreamRoleId.BrowsePreviewAudioDownmix:
      case StreamRoleId.EditProxyFull:
        logger.debug(`createCmafSegmentSourceForItem: using fragmented MP4 CMAF source for ${streamRoleId}`);
        cmafSegmentSources.push(
          new MimirFragmentedMp4CmafSegmentSource(
            fetchSegmentWithPrefetch,
            abortSignal,
            description,
            fractionToFuzzy(media.segmentDuration),
            itemDuration.getObservable(),
            segmentCount.getObservable(),
            streamRoleId,
          )
        );
        break;
      case StreamRoleId.LiveRecordHighresTs:
      case StreamRoleId.LiveRecordWebproxyTs:
      case StreamRoleId.LiveRecordAudioOnlyProxyTs:
      case StreamRoleId.LiveRecordAudioOnlyHighresTs:
        logger.debug(`createCmafSegmentSourceForItem: using TS segments CMAF source with FLAC downmix for ${streamRoleId}`);
        cmafSegmentSources.push(
          new MimirTsWithFlacDownMixCmafSource(
            logger,
            playerDebugId,
            fetchSegmentWithPrefetch,
            asyncTransmuxerProvider,
            abortSignal,
            description,
            fractionToFuzzy(media.segmentDuration),
            itemDuration.getObservable(),
            segmentCount.getObservable(),
            tsAudioController.matrix,
            audioChannelCount,
            services.inspectTs,
          )
        );
        break;
      default:
        throw new Error(`Unsupported StreamRoleId: ${streamRoleId}`);
    }
  }

  logger.debug('createCmafSegmentSourceForItem: stating live item media monitoring');

  liveItemMediaMonitoring(
    logger,
    api,
    updateFromMediaAvailability,
    abortSignal,
    itemId,
    playableItemGetOptions,
    getPeakOffset,
    addPeaksDelta,
  ).catch((error) => {
    if (!isAbortError(error)) {
      logger.error(`Live item media monitoring thread failed: ${exceptionToString(error)}`);
    }
  });

  logger.debug('createCmafSegmentSourceForItem: done');

  return cmafSegmentSources;
}


/// End of public API

type FetchSegment = (signal: AbortSignal, segmentIndex: number) => Promise<Uint8Array>;

class MimirFragmentedMp4CmafSegmentSource implements CmafSegmentSource {
  private initSegment : Uint8Array | null = null;

  constructor(
    private fetchSegment: (abortSignal: AbortSignal, index: number) => Promise<Uint8Array>,
    private abortSignal: AbortSignal,
    private description: string,
    private segmentDuration: FuzzyValue,
    private itemDuration: Observable<FuzzyValue>,
    private segmentCount: Observable<number>,
    private streamRoleId:
      StreamRoleId.BrowsePreviewVideo |
      StreamRoleId.BrowsePreviewVideoAudioDownMix |
      StreamRoleId.BrowsePreviewAudioDownmix |
      StreamRoleId.EditProxyFull
  ) {
  }

  humanReadableDescription(): string {
    return this.description;
  }

  async codec(): Promise<string> {
    switch(this.streamRoleId) {
      case StreamRoleId.EditProxyFull:
      case StreamRoleId.BrowsePreviewVideoAudioDownMix:
        return CodecString.MP4_VIDEO_WITH_AUDIO;
      case StreamRoleId.BrowsePreviewAudioDownmix:
        return CodecString.MP4_AUDIO_AAC_LOW_COMPLEXITY;
      case StreamRoleId.BrowsePreviewVideo:
        return CodecString.MP4_VIDEO_H264_BASELINE_AVC_20;
    }
  }

  async createIterator(
    startTime: FuzzyValue | null,
    _evictAll: () => void,
  ): Promise<CmafSegmentIterator> {
    const firstSegmentIndex = calculateStartSegmentIndex(startTime, this.segmentDuration);
    let segmentIndex = firstSegmentIndex;

    const abortController = new AbortController();
    const abortSignal = abortController.signal;

    // Make sure the abort controller for the cmaf segment source triggers the iterator
    // abort controller
    this.abortSignal.addEventListener('abort', () => abortController.abort());

    let firstIteratorResult : Uint8Array | null = await this.getInitSegment(abortSignal);

    return {
      startPosition : (): FuzzyValue => {
        return multiplyFuzzy(integerToFuzzy(firstSegmentIndex), this.segmentDuration);
      },
      next : async (bufferedRanges: FuzzyBounds[]): Promise<CmafSegment | 'closed'> => {
        try {
          if (firstIteratorResult) {
            const bytes = firstIteratorResult;
            firstIteratorResult = null;
            return { bytes, timeRange: null };
          }

          while (true) {
            throwIfAborted(abortSignal);

            const timeRange : FuzzyBounds = {
              start: multiplyFuzzy(integerToFuzzy(segmentIndex), this.segmentDuration),
              end: multiplyFuzzy(integerToFuzzy(segmentIndex + 1), this.segmentDuration),
            };


            // segment + 1 because segment 0 is an init segment that does not provide media
            const segmentToFetch = segmentIndex + 1;

            while (segmentToFetch >= this.segmentCount.currentValue()) {
              await this.segmentCount.waitForChange(abortSignal);
            }

            segmentIndex += 1;

            if (isRangeCovered(bufferedRanges, timeRange)) {
              continue;
            }

            const bytes = await this.fetchSegment(abortSignal, segmentToFetch);

            return { bytes, timeRange };
          }
        } catch (e) {
          if (isAbortError(e)) {
            return 'closed';
          }
          throw e;
        }
      },
      close() {
        abortController.abort();
      }
    };
  }

  async duration(): Promise<FuzzyValue> {
    return this.itemDuration.currentValue();
  }

  private async getInitSegment(abortSignal: AbortSignal): Promise<Uint8Array> {
    while (this.initSegment === null) {
      throwIfAborted(abortSignal);
      this.initSegment = await this.fetchSegment(abortSignal, 0);
    }

    return this.initSegment;
  }
}

class MimirTsWithFlacDownMixCmafSource implements CmafSegmentSource {
  private transmuxer : AsyncTransmuxer;
  private firstTsSegment : Uint8Array | null = null;
  private firstCmafSegment : Uint8Array | null = null;
  private firstSegmentInfo : TsSegmentInfo | null = null;

  constructor(
    private logger: Logger,
    private playerDebugId: string,
    private fetchSegment: (abortSignal: AbortSignal, index: number) => Promise<Uint8Array>,
    asyncTransmuxerProvider: AsyncTransmuxerProvider,
    private abortSignal: AbortSignal,
    private description: string,
    private segmentDuration: FuzzyValue,
    private itemDuration: Observable<FuzzyValue>,
    private segmentCount: Observable<number>,
    private mixMatrix: Observable<number[][]>,
    private audioChannelCount: ObservableState<number | null>,
    private inspectTs: (bytes: Uint8Array, firstSegment: boolean) => TsSegmentInfo | null,
  ) {
    this.transmuxer = asyncTransmuxerProvider(abortSignal);
  }

  humanReadableDescription(): string {
    return this.description;
  }

  async codec(): Promise<string> {
    const segmentInfo = await this.getFirstSegmentInfo();
    const hasAudio = segmentInfo?.hasAudio ?? false;
    const hasVideo = segmentInfo?.hasVideo ?? false;

    if (hasAudio) {
      if (hasVideo) {
        return CodecString.MP4_VIDEO_WITH_FLAC;
      } else {
        return CodecString.MP4_AUDIO_FLAC;
      }
    } else if (hasVideo) {
      return CodecString.MP4_VIDEO_H264_BASELINE_AVC_20;
    } else {
      throw new Error('Has neither audio nor video');
    }
  }

  private async getFirstTsSegment(abortSignal: AbortSignal): Promise<Uint8Array> {
    if (this.firstTsSegment === null) {
      this.firstTsSegment = await this.fetchSegment(abortSignal, 0);
    }
    return this.firstTsSegment;
  }

  private async getFirstSegmentInfo(): Promise<TsSegmentInfo | null> {
    if (this.firstSegmentInfo === null) {
      const firstTsSegment = await this.getFirstTsSegment((new AbortController()).signal);
      const info = this.inspectTs(firstTsSegment, true);
      this.firstSegmentInfo = info;
    }
    return this.firstSegmentInfo;
  }

  async createIterator(
    startTime: FuzzyValue | null,
    evictAll: () => void,
  ): Promise<CmafSegmentIterator> {
    const firstSegmentIndex = calculateStartSegmentIndex(startTime, this.segmentDuration);
    let segmentIndex = firstSegmentIndex;
    const abortController = new AbortController();
    const abortSignal = abortController.signal;
    const cleanables : Cleanable[] = [];

    const iteratorMixMatrix = this.mixMatrix.currentValue();

    cleanables.push(this.mixMatrix.observe((newValue) => {
      if (mixMatrixIsEquivalent(newValue, iteratorMixMatrix)) {
        return;
      }
      this.logger.info(`FLAC downmix matrix changed ${JSON.stringify({ newValue, oldValue: iteratorMixMatrix })}. Aborting iterator`);
      abortController.abort();
      evictAll();
    }));

    // Make sure the abort controller for the cmaf segment source triggers the iterator
    // abort controller
    this.abortSignal.addEventListener('abort', () => abortController.abort());

    if (this.firstCmafSegment === null) {
      const firstSegment = await this.getFirstTsSegment(abortSignal);
      // Note: Since this is just creating the initialization segment which actually does not provide any audio samples it does not
      // matter what the mix matrix is at this point so we can just reuse the CMAF init segment.
      const transmuxOutput =
        await this.transmuxer(
          this.playerDebugId,
          firstSegment,
          iteratorMixMatrix,
          false
        );
      this.firstCmafSegment = transmuxOutput.initialization;
      this.audioChannelCount.value = transmuxOutput.audioChannelCount;
      invariant(this.firstCmafSegment !== null, 'Expected init segment');
    }

    const pendingSegments : CmafSegment[] = [{ bytes: this.firstCmafSegment, timeRange: null }];

    let expectedNextSegmentIndex = 0;

    return {
      startPosition: (): FuzzyValue => {
        return multiplyFuzzy(integerToFuzzy(firstSegmentIndex), this.segmentDuration);
      },
      next : async (bufferedRanges: FuzzyBounds[]): Promise<CmafSegment | 'closed'> => {
        try {
          if (pendingSegments.length > 0) {
            const [segment] = pendingSegments.splice(0, 1);
            return segment;
          }

          while (true) {
            throwIfAborted(abortSignal);

            const timeRange : FuzzyBounds = {
              start: multiplyFuzzy(integerToFuzzy(segmentIndex), this.segmentDuration),
              end: multiplyFuzzy(integerToFuzzy(segmentIndex + 1), this.segmentDuration),
            };

            const segmentToFetch = segmentIndex;

            while (segmentToFetch >= this.segmentCount.currentValue()) {
              await this.segmentCount.waitForChange(abortSignal);
            }

            segmentIndex += 1;

            if (isRangeCovered(bufferedRanges, timeRange)) {
              this.logger.debug(`Segment ${segmentToFetch} is already buffered. timeRange=${humanReadableRange(timeRange)}, buffered=${humanReadableRanges(bufferedRanges)}`);
              continue;
            }

            // TODO: Instead of transmuxing an entire segment and discarding all but the last
            // packets going IN to the AAC decoder we should instead be able to just discard
            // the first 1024 FLAC samples of the samples coming OUT from the decoder and not
            // have to transmux a leading segment.

            const promises : Promise<Uint8Array>[] = [];
            let onlyUseLastAacPacket: boolean = false;
            if ((segmentToFetch != expectedNextSegmentIndex) && (segmentToFetch > 0)) {
              promises.push(this.fetchSegment(abortSignal, segmentToFetch - 1));
              onlyUseLastAacPacket = true;
            }
            expectedNextSegmentIndex = segmentToFetch + 1;

            promises.push(this.fetchSegment(abortSignal, segmentToFetch));

            let cmafBytes : Uint8Array | null = null;
            for (const tsSegment of await Promise.all(promises)) {
              const transmuxOutput =
                await this.transmuxer(
                  this.playerDebugId,
                  tsSegment,
                  iteratorMixMatrix,
                  onlyUseLastAacPacket
                );
              onlyUseLastAacPacket = false;
              cmafBytes = transmuxOutput.mediaSegment;
            }

            invariant(cmafBytes !== null, 'Expected at least on fragmented mp4 buffer');

            throwIfAborted(abortSignal);

            return { bytes: cmafBytes, timeRange };
          }
        } catch (e) {
          if (isAbortError(e)) {
            return 'closed';
          }
          throw e;
        }
      },
      close: () => {
        cleanables.splice(0).map((cleanable) => cleanable.cleanup());
        abortController.abort();
      }
    };
  }

  async duration(): Promise<FuzzyValue> {
    return this.itemDuration.currentValue();
  }
}

function trimZeroes(matrix: number[][]): number[][] {
  const m = cloneDeep(matrix);
  for (let i = 0; i < m.length; i++) {
    const row = m[i];
    // Remove trailing zeroes from each row
    while ((row.length > 0) && (row[row.length - 1] === 0.0)) {
      row.pop();
    }
  }
  // Remove trailing empty rows
  while ((m.length > 0) && (m[m.length - 1].length === 0)) {
    m.pop();
  }
  return m;
}

function mixMatrixIsEquivalent(a: number[][], b: number[][]): boolean {
  return isEqual(trimZeroes(a), trimZeroes(b));
}

function calculateStartSegmentIndex(
  startTime: FuzzyValue | null,
  segmentDuration: FuzzyValue,
): number {
  if (startTime === null) {
    return 0;
  } else {
    return Math.max(0, Math.floor(divideFuzzy(startTime, segmentDuration).approximate));
  }
}


export function makeSegmentPrefetcher(
  outerLogger: Logger,
  fetchSegment: FetchSegment,
  cacheSize: number,
  segmentCount: Observable<number>,
): FetchSegment {
  interface PrefetchPending {
    type: 'pending',
    resolve: ((bytes: Uint8Array) => void)[],
    reject: ((err: any) => void)[]
  }

  interface PrefetchResolved {
    type: 'resolved',
    segment: Uint8Array,
  }

  interface PrefetchRejected {
    type: 'rejected',
    err: any,
  }

  interface Prefetch {
    segmentIndex: number,
    useCount: number,
    inner: PrefetchPending | PrefetchResolved | PrefetchRejected,
  }

  const prefetches : Prefetch[] = [];

  const logger = new PrefixedLogger(outerLogger, '[prefetch]');
  return async (abortSignal: AbortSignal, segmentIndex: number): Promise<Uint8Array> => {
    const getPromiseForPrefetch = (prefetch: Prefetch): Promise<Uint8Array> => {
      prefetch.useCount += 1;
      if (prefetch.inner.type === 'resolved') {
        logger.debug(`fetchSegmentWithPrefetch(${segmentIndex}): already resolved`);
        return Promise.resolve(prefetch.inner.segment);
      } else if (prefetch.inner.type === 'rejected') {
        logger.debug(`fetchSegmentWithPrefetch(${segmentIndex}): already rejected`);
        return Promise.reject(prefetch.inner.err);
      } else {
        logger.debug(`fetchSegmentWithPrefetch(${segmentIndex}): piggy back on pending`);
        const pending = prefetch.inner;
        return new Promise<Uint8Array>((resolve, reject) => {
          pending.resolve.push((buffer) => resolve(buffer));
          pending.reject.push((err) => reject(err));
        });
      }
    };

    const getOrCreatePrefetch = (abortSignal: AbortSignal, createSegmentIndex: number): Prefetch => {
      const existingPrefetch = prefetches.find((p) => p.segmentIndex === createSegmentIndex);
      if (existingPrefetch !== undefined) {
        logger.debug(`prefetch already exists for ${createSegmentIndex}`);
        return existingPrefetch;
      }

      const prefetch : Prefetch = {
        segmentIndex: createSegmentIndex,
        useCount: 0,
        inner: {
          type: 'pending',
          resolve: [],
          reject: [],
        }
      };

      const removeThisPrefetch = () => {
        const index = prefetches.findIndex((p) => p.segmentIndex === segmentIndex);
        if (index !== -1) {
          prefetches.splice(index, index + 1);
        }
      };

      logger.debug(`dispatching request for ${createSegmentIndex}`);
      fetchSegment(abortSignal, createSegmentIndex)
        .then((buffer) => {
          invariant(prefetch.inner.type === 'pending', 'Should be pending');
          logger.debug(`resolving pending prefech for ${createSegmentIndex}`);
          prefetch.inner.resolve.forEach((resolve) => resolve(buffer));
          prefetch.inner = {
            type: 'resolved',
            segment: buffer,
          };
        })
        .catch((error) => {
          invariant(prefetch.inner.type === 'pending', 'Should be pending');
          logger.debug(`rejecting pending prefech for ${createSegmentIndex}`);
          prefetch.inner.reject.forEach((reject) => reject(error));
          prefetch.inner = {
            type: 'rejected',
            err: error,
          };
        })
        .finally(() => {
          abortSignal.removeEventListener('abort', removeThisPrefetch);
        });

      prefetches.push(prefetch);

      abortSignal.addEventListener('abort', removeThisPrefetch);

      while (prefetches.length > cacheSize) {
        const [oldest] = prefetches.splice(0, 1);
        if (oldest.useCount === 0) {
          logger.debug(`Unused segment prefetch: ${oldest.segmentIndex}`);
        }
      }

      return prefetch;
    };

    const prefetch = getOrCreatePrefetch(abortSignal, segmentIndex);
    const promise = getPromiseForPrefetch(prefetch);
    if (segmentCount.currentValue() > segmentIndex + 1) {
      getOrCreatePrefetch(abortSignal, segmentIndex + 1);
    }
    return promise;
  };
}

